import { useState, useMemo, useRef, useCallback } from 'react';
import { css } from '@emotion/react';
import type { TeamChatTypes } from '@frontend/api-team-chat';
import { useTranslation } from '@frontend/i18n';
import { Chip, Avatar, Text, ContentLoader, useOnClickOutside } from '@frontend/design-system';
import { useUserSearch } from '../../../hooks/use-user-search';
import { useTeamChatSelector } from '../../../providers/team-chat.provider';
import { getUserFullName, type Target } from '../../../utils';
import { CustomCombobox } from '../../common/custom-combobox/custom-combobox';
import { ConversationDropdownMenuItem } from './conversation-dropdown-menu-item';

/*
  This new conversation component manages the new conversation creation.
  Since we display users and groups in the same list, we need to differentiate between them. StreamUser and StreamConversation
  When one is selected we need to hide the other one, this is managed by filterType<'DM'|'Group'|''>.
  This filterType is updated when user types '@' or '#' user selected either a user or a conversation from the 
  dropdown list.
 */

type TargetOption = TeamChatTypes.User | TeamChatTypes.Conversation;
export const isUser = (target: TargetOption): target is TeamChatTypes.User => 'userID' in target;
export const isConversation = (target: TargetOption): target is TeamChatTypes.Conversation => 'channelId' in target;

type Props = {
  selectedTarget: Target | undefined;
  onChange: (target: Target | undefined) => void;
  /**
   * Whether the member dropdown auto-opens on mount. Defaults to `true` for user-initiated new
   * conversations. Pass `false` when a target is preselected programmatically (e.g. via
   * `openConversationWithUser`) so the dropdown doesn't pop open unnecessarily.
   */
  openOnMount?: boolean;
};
export const ConversationMemberSelection = ({
  selectedTarget,
  onChange: onTargetChange,
  openOnMount = true,
}: Props) => {
  const { t } = useTranslation('team-chat');
  const [shouldShowNumberAdornment, setShouldShowNumberAdornment] = useState<boolean>(false);

  const conversations = useTeamChatSelector((ctx) => ctx.conversations);
  const openDynamicConversation = useTeamChatSelector((ctx) => ctx.openDynamicConversation);
  const helpers = useTeamChatSelector((ctx) => ctx.helpers);
  const isCreatingConversation = useTeamChatSelector((ctx) => ctx.isCreatingConversation);
  const currentUser = useTeamChatSelector((ctx) => ctx.user);
  const shouldFetchConversationUsers = useTeamChatSelector((ctx) => ctx.shouldFetchConversationUsers);
  const {
    onChangeSearchTerm,
    searchTerm,
    usersToDisplay,
    isLoading: isLoadingUsers,
    isError: isUsersError,
    refetchUsers,
  } = useUserSearch(true);

  const comboboxRef = useRef(null);
  const isMenuOpenRef = useRef(false);

  useOnClickOutside({
    ref: comboboxRef,
    handler: () => {
      if (isMenuOpenRef.current) {
        return;
      }
      setShouldShowNumberAdornment(true);
    },
  });

  const handleAddTarget = useCallback(
    async (item: TargetOption) => {
      if (!currentUser) {
        return;
      }

      if (isConversation(item)) {
        const conversation = item;
        onTargetChange({ type: 'group', conversation: conversation });
        openDynamicConversation(conversation.channelId);
      } else {
        const user = item;
        const selectedUsers = [...(selectedTarget?.type === 'dm' ? selectedTarget.users : []), user];
        onTargetChange({ type: 'dm', users: selectedUsers });
        const conversation = helpers.getConversationByMemberIds(
          [...selectedUsers.map((member) => member.userID)],
          'dm'
        );
        openDynamicConversation(conversation?.channelId ?? undefined);
      }
    },
    [helpers, onTargetChange, openDynamicConversation, selectedTarget, currentUser]
  );

  const handleRemoveTarget = useCallback(
    async (item: TargetOption) => {
      if (!currentUser) {
        return;
      }
      if (isConversation(item)) {
        onTargetChange(undefined);
        openDynamicConversation(undefined);
      } else if (isUser(item)) {
        const remainingUsers = (selectedTarget?.type === 'dm' ? selectedTarget.users : []).filter(
          (user) => user.userID !== item.userID
        );
        onTargetChange({ type: 'dm', users: remainingUsers });
        if (remainingUsers.length === 0) {
          openDynamicConversation(undefined);
          onTargetChange(undefined);
        } else {
          const conversation = helpers.getConversationByMemberIds(
            [...remainingUsers.map((member) => member.userID)],
            'dm'
          );
          openDynamicConversation(conversation?.channelId ?? undefined);
        }
      }
    },
    [helpers, onTargetChange, openDynamicConversation, selectedTarget, currentUser]
  );

  const availableTargets = useMemo(() => {
    const trimmedSearchTerm = searchTerm.trim();
    // When there is an api call we want to show loading state, so we return empty array while fetching
    if (isLoadingUsers) return [];

    if (selectedTarget?.type === 'group') {
      return conversations?.groups ?? [];
    }

    if (selectedTarget?.type === 'dm' || trimmedSearchTerm.startsWith('@')) {
      const selectedUsersIds = selectedTarget?.users ? selectedTarget.users.map((user) => user.userID) : [];
      return usersToDisplay.filter((user) => !selectedUsersIds.includes(user.userID));
    }

    if (trimmedSearchTerm.startsWith('#')) {
      const input = trimmedSearchTerm.trim().slice(1).toLowerCase().replace('#', '');
      return conversations?.groups?.filter((group) => group.name.toLowerCase().includes(input));
    } else {
      return [...(conversations?.groups ?? []), ...usersToDisplay];
    }
  }, [searchTerm, isLoadingUsers, selectedTarget, usersToDisplay, conversations?.groups]);

  const RecipientChip = useMemo(
    () =>
      // eslint-disable-next-line react/display-name
      ({ children, onClick }: { children?: string; onClick?: () => void }) => {
        if (selectedTarget?.type === 'group') {
          return (
            <Chip.Removable onClick={() => onClick?.()}>
              <Text> # {children}</Text>
            </Chip.Removable>
          );
        } else {
          //I guess we just show the person chip if it's anything other than a group
          return (
            <Chip.Person
              avatar={
                <Avatar
                  size='xxs'
                  name={children}
                  bypassColor={{ fill: 'var(--fabric-color-neutral-30)', text: 'var(--fabric-color-neutral-00)' }}
                  isUser
                />
              }
              onClick={onClick}
              css={entityChipStyles}
            >
              {children}
            </Chip.Person>
          );
        }
      },
    [selectedTarget?.type]
  );

  const placeholder =
    selectedTarget?.type === 'dm'
      ? t('@someone')
      : selectedTarget?.type === 'group'
      ? t('#a-channel')
      : t('#a-channel or @someone');

  const startAdornment = useMemo(
    () => (
      <span css={startAdornmentStyle}>
        <Text size='large'>{t('To:')}</Text>
        {selectedTarget?.type === 'dm' && selectedTarget.users.length > 2 && shouldShowNumberAdornment && (
          <Text
            size='small'
            weight='bold'
            css={adornmentCountStyle}
            onClick={() => setShouldShowNumberAdornment(false)}
          >
            {selectedTarget.users.length - 2}
          </Text>
        )}
      </span>
    ),
    [selectedTarget, shouldShowNumberAdornment, t]
  );

  const selectedTargetItems = useMemo(() => {
    if (!selectedTarget) {
      return [];
    }
    if (selectedTarget.type === 'dm') {
      if (selectedTarget.users.length > 2 && shouldShowNumberAdornment) {
        return selectedTarget.users.slice(selectedTarget.users.length - 2, selectedTarget.users.length);
      }
      return selectedTarget.users;
    }
    if (selectedTarget.type === 'group') {
      return [selectedTarget.conversation];
    }
    return [];
  }, [shouldShowNumberAdornment, selectedTarget]);

  const customFilter = useCallback((option: TargetOption, input: string) => {
    if (input.startsWith('#')) {
      return isConversation(option) && option.name.toLowerCase().includes(input.slice(1).toLowerCase());
    } else if (input.startsWith('@')) {
      return (
        isUser(option) &&
        getUserFullName(option.firstName, option.lastName).toLowerCase().includes(input.slice(1).toLowerCase())
      );
    } else {
      return isUser(option)
        ? getUserFullName(option.firstName, option.lastName).toLowerCase().includes(input.toLowerCase())
        : option.name.toLowerCase().includes(input.toLowerCase());
    }
  }, []);

  return (
    <div ref={comboboxRef} css={comboboxParentStyle}>
      <CustomCombobox<TargetOption>
        clearable={false}
        onInputChange={onChangeSearchTerm}
        customOptionsFilter={customFilter}
        options={availableTargets ?? []}
        placeholder={selectedTarget?.type === 'group' ? '' : placeholder}
        name={t('channel-search')}
        accessor={(item: TargetOption) => (isUser(item) ? `${item.firstName} ${item.lastName}` : item?.name)}
        className='chip-combobox'
        onOptionSelect={handleAddTarget}
        onOptionRemove={handleRemoveTarget}
        tags={selectedTargetItems ?? []}
        ChipComponent={RecipientChip}
        MenuItem={ConversationDropdownMenuItem}
        startAdornment={startAdornment}
        menuStyles={css({ height: 256, marginTop: 'var(--fabric-space-offset-md)' })}
        isMenuOpenRef={isMenuOpenRef}
        openOnMount={openOnMount}
        {...(shouldFetchConversationUsers && {
          shouldRenderListWhenEmpty: shouldFetchConversationUsers && searchTerm.length === searchTerm.trim().length,
          isLoading: isLoadingUsers,
          emptyStateConfig: {
            header:
              !usersToDisplay.length && !searchTerm.trim().length
                ? t('Search for your co-workers by typing their name')
                : t('No users found with the given name'),
            type: 'generic',
            disableGraphic: true,
          },
          isError: isUsersError,
          onRetry: refetchUsers,
        })}
      />
      <ContentLoader show={isCreatingConversation} message={t('Creating new conversation')} />
    </div>
  );
};

const entityChipStyles = css({
  border: 'none',
  backgroundColor: 'var(--fabric-color-action-primary-background-subtle)',
  svg: {
    color: 'var(--fabric-color-icon-secondary)',
  },
});

const adornmentCountStyle = css({
  width: '24px',
  height: '24px',
  borderRadius: 'var(--fabric-radius-md)',
  border: '1.5px solid var(--fabric-color-border-strong)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const comboboxParentStyle = css({
  '.chip-combobox': {
    border: '1px solid var(--fabric-color-border-subtle)',
    borderLeft: 0,
    borderRight: 0,
    display: 'flex',
    gap: 'var(--fabric-space-gap-xs)',
    padding: 'var(--fabric-space-padding-md)',
    boxShadow: 'none !important',
    outlineColor: 'none',
    transition: 'none',
    borderRadius: 0,
    height: 'unset',
  },
});

const startAdornmentStyle = css({
  display: 'flex',
  alignSelf: 'baseline',
  gap: 'var(--fabric-space-gap-xs)',
});
